// src/pages/IO/ExportXLS.tsx

import { ImportExportTwoTone } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import {
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Link } from 'react-router-dom'

import { UserSingle } from '@/types/domain'

import {
  isFilterMatch,
  notifyError,
  notifySuccess,
  userLocales,
} from '../../api'
import { HStack, KPage, Section } from '../../components'
import { useIoApi, useUser, useOperator, OperatorData } from '../../hooks'

const DAILY = 'd'
const MONTHLY = 'm'

const fmtDur = (start, end) => {
  if (!start || !end) return '—'
  const sec = Math.max(0, end - start)
  const mm = Math.floor(sec / 60)
    .toString()
    .padStart(2, '0')
  const ss = Math.floor(sec % 60)
    .toString()
    .padStart(2, '0')
  return `${mm}:${ss}`
}

const fmtLiveDur = (start, end) =>
  fmtDur(start, end ?? Math.floor(Date.now() / 1000))

const getReportingDateISO = () => {
  const now = new Date()
  const localEod = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 1,
    23,
    59,
    59,
    0
  )
  return localEod.toISOString()
}

const splitEmails = value =>
  (value || '')
    .split(/[,;\s]+/)
    .map(v => v.trim())
    .filter(Boolean)

const joinEmails = arr => arr.join(',')

const addEmailsFromInput = (list, inputValue) => {
  const parts = splitEmails(inputValue)
  const merged = splitEmails([...list, ...parts].join(','))
  return merged
}

export const ExportXLS = () => {
  const { exportXls, getIoJobStatus } = useIoApi()

  const [jobId, setJobId] = useState(null)
  const [status, setStatus] = useState(null)
  const [startedAt, setStartedAt] = useState(null)
  const [finishedAt, setFinishedAt] = useState(null)

  const timerRef = useRef(null)

  const [tick, setTick] = useState(0)

  const [granularity, setGranularity] = useState<string>(MONTHLY)
  const [locales, setLocales] = useState<string>(userLocales.replace('-', '_'))

  const opData: OperatorData = useOperator()

  const [opId, setOpId] = useState<number | null>(null)
  const [selectedFolderIds, setSelectedFolderIds] = useState<number[]>([])

  const [recipients, setRecipients] = useState({ to: '', cc: '', bcc: '' })

  const [toEmails, setToEmails] = useState<string[]>([])
  const [ccEmails, setCcEmails] = useState<string[]>([])
  const [bccEmails, setBccEmails] = useState<string[]>([])

  const [toInput, setToInput] = useState<string>('')
  const [ccInput, setCcInput] = useState<string>('')
  const [bccInput, setBccInput] = useState<string>('')

  const [cols, setCols] = useState({
    date: true,
    actual_consumption: true,
    counter_reading: true,
    emissions: true,
    area_consumption: true,
    employee_consumption: true,
    actual_costs: true,
    area_costs: true,
    employee_costs: true,
  })

  useEffect(() => {
    if (!opData?.id || !opData?.email) return
    setOpId(opData?.id)

    const arr = splitEmails(opData?.email)
    setRecipients({
      to: joinEmails(arr),
      cc: '',
      bcc: '',
    })

    setToEmails(arr)
  }, [opData])

  const foo: UserSingle = useUser(opId)

  const elapsedRef = useRef(null)
  const running: boolean = status === 'PENDING' || status === 'RUNNING'

  const [filter, setFilter] = useState<string>('')

  const onChangeFilter = (event: ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value)

  useEffect(() => {
    if (running && startedAt) {
      if (!elapsedRef.current)
        elapsedRef.current = setInterval(() => setTick(t => t + 1), 1000)
    } else {
      if (elapsedRef.current) {
        clearInterval(elapsedRef.current)
        elapsedRef.current = null
      }
    }

    return () => {
      if (elapsedRef.current) {
        clearInterval(elapsedRef.current)
        elapsedRef.current = null
      }
    }
  }, [running, startedAt])

  const clearTimer = () => {
    if (!timerRef.current) return
    clearInterval(timerRef.current)
    timerRef.current = null
  }

  const pollStatus = useCallback(
    async jid => {
      if (!jid) return
      try {
        const res = await getIoJobStatus(jid)
        setStatus(res.status || null)
        setStartedAt(res.startedAt || null)
        setFinishedAt(res.finishedAt || null)

        if (res.status === 'DONE') {
          notifySuccess(`Export fertig: ${tick}s`)
          clearTimer()
        } else if (res.status === 'FAILED') {
          notifyError('Export fehlgeschlagen')
          clearTimer()
        }
      } catch (error) {
        notifyError(`Status-Abfrage fehlgeschlagen: ${error}`)
        clearTimer()
      }
    },
    [getIoJobStatus, tick]
  )

  useEffect(() => clearTimer(), [])
  useEffect(() => setSelectedFolderIds([]), [granularity])

  const onClick = useCallback(async () => {
    try {
      const { jobId: jid } = await exportXls(
        opId,
        getReportingDateISO(),
        selectedFolderIds,
        granularity,
        recipients,
        locales,
        cols
      )
      setJobId(jid)
      setStatus('PENDING')
      setStartedAt(null)
      setFinishedAt(null)
      notifySuccess('Export gestartet')

      await pollStatus(jid)

      if (!timerRef.current)
        timerRef.current = setInterval(() => pollStatus(jid), 3000)
    } catch (error) {
      notifyError(`Start fehlgeschlagen: ${error.message}`)
      clearTimer()
    }
  }, [
    cols,
    exportXls,
    pollStatus,
    recipients,
    granularity,
    locales,
    opId,
    selectedFolderIds,
  ])

  const ALL_SENSORS_SELECTED = selectedFolderIds.length === foo?.folders.length

  const FILTER_SEARCH = item =>
    filter
      ? isFilterMatch(filter, item.name) ||
        isFilterMatch(filter, item.meter_number) ||
        isFilterMatch(filter, item.device_id) ||
        Number(filter) === Number(item.entity_id)
      : item

  const colsData = [
    { label: 'Datum', accessor: 'date' },
    { label: 'Verbrauch', accessor: 'actual_consumption' },
    { label: 'Zählerstand', accessor: 'counter_reading' },
    { label: 'CO₂-Emissionen', accessor: 'emissions' },
    { label: 'Verbrauch / m²', accessor: 'area_consumption' },
    { label: `Verbrauch / MA`, accessor: 'employee_consumption' },
    { label: 'Kosten', accessor: 'actual_costs' },
    { label: 'Kosten / m²', accessor: 'area_costs' },
    { label: `Kosten / MA`, accessor: 'employee_costs' },
  ]

  return (
    <KPage
      avatar={
        <Avatar>
          <ImportExportTwoTone />
        </Avatar>
      }
      title={
        <Breadcrumbs sx={{ mx: 1, my: 2 }}>
          <Link to={'/io'}>
            <Typography>Exporte</Typography>
          </Link>
          <Typography>Zähler Informationen</Typography>
        </Breadcrumbs>
      }
    >
      <Stack sx={{ mb: 2 }}>
        <Typography variant={'h4'}>
          Zähler Informationen Export (XLS)
        </Typography>

        <Typography sx={{ my: 1 }}>
          Exportiert alle Zähler Informationen in eine Excel Datei. Die Excel
          Datei wird per Email an die Empfänger verschickt.
        </Typography>
      </Stack>

      <Section title={'Zeitraum'} open={false} sx={{ display: 'none' }}>
        <TextField
          disabled
          fullWidth
          label={'Vortag'}
          value={`${new Date(getReportingDateISO()).toLocaleDateString(
            userLocales,
            { day: '2-digit', month: 'long', year: 'numeric' }
          )} (ISO: ${getReportingDateISO()})`}
        />

        <Alert severity={'info'} sx={{ mt: 2 }}>
          <AlertTitle>Hinweis zur Zeitzone (MEZ/MESZ)</AlertTitle>
          <Typography>
            Unsere Systeme arbeiten standardmäßig mit UTC (Coordinated Universal
            Time).
          </Typography>
          <Typography>In Deutschland gilt je nach Jahreszeit:</Typography>
          <Typography>
            MEZ – Mitteleuropäische Zeit (UTC+1) in den Wintermonaten
          </Typography>
          <Typography>
            MESZ – Mitteleuropäische Sommerzeit (UTC+2) in den Sommermonaten
          </Typography>
          <Typography>
            Das bedeutet: Server-Logs werden in UTC geführt, während lokale
            Uhren in Deutschland im Winter eine Stunde voraus, im Sommer zwei
            Stunden voraus sind. Wenn Sie eine Uhrzeit aus einem Logfile mit
            Ihrer lokalen Uhr vergleichen, berücksichtigen Sie bitte die
            Zeitverschiebung.
          </Typography>
        </Alert>
      </Section>

      <Section title={'Granularität'}>
        <RadioGroup
          value={granularity}
          onChange={e => setGranularity(e.target.value)}
        >
          <FormControlLabel
            value={DAILY}
            control={<Radio />}
            label={'Täglich'}
          />
          <FormControlLabel
            value={MONTHLY}
            control={<Radio />}
            label={'Monatlich'}
          />
        </RadioGroup>
      </Section>

      <Section title={'Kennzahlen'} open={false}>
        <List>
          {colsData.map((cell, key) => {
            const { accessor, label } = cell
            const checked = cols[accessor]

            const toggle = () =>
              setCols(prev => {
                const newVO = { ...prev }
                newVO[accessor] = !prev[accessor]

                return newVO
              })

            return (
              <ListItem key={label} sx={{ p: 0 }}>
                <ListItemButton
                  disabled={key === 0}
                  onClick={toggle}
                  sx={{ py: 0 }}
                >
                  <ListItemText>{label}</ListItemText>

                  <Checkbox checked={checked} disabled={key === 0} />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Section>

      <Section
        open={false}
        title={
          <Fragment>
            Gebäude{' '}
            <Chip
              label={`${selectedFolderIds.length}/${foo?.folders.length ? foo?.folders.length : 0}`}
            />
          </Fragment>
        }
      >
        <Collapse in={granularity === DAILY}>
          <Alert severity={'info'} sx={{ mb: 1 }}>
            Granularität &quot;Täglich&quot; unterstützt die Auswahl eines
            einzelnen Gebäudes.
          </Alert>
        </Collapse>

        <List
          sx={{
            border: 'solid 1px #ccc',
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          <ListSubheader sx={{ py: 0 }}>
            <HStack>
              <Input
                fullWidth
                onChange={onChangeFilter}
                placeholder={'Filter'}
                sx={{ py: 1 }}
                value={filter}
              />

              <Divider orientation={'vertical'} sx={{ height: 30 }} />
              <Tooltip title={'Massen Auswahl'}>
                <Switch
                  checked={ALL_SENSORS_SELECTED}
                  disabled={filter !== '' || granularity === DAILY}
                  onChange={() => {
                    if (ALL_SENSORS_SELECTED) {
                      setSelectedFolderIds([])
                    } else {
                      setSelectedFolderIds(
                        foo?.folders.map(f => Number(f.entity_id))
                      )
                    }
                  }}
                />
              </Tooltip>
            </HStack>
          </ListSubheader>

          {foo?.folders
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter(FILTER_SEARCH)
            .map(f => (
              <ListItemButton
                key={f.entity_id}
                onClick={() => {
                  if (granularity === MONTHLY) {
                    if (selectedFolderIds.includes(f.entity_id)) {
                      setSelectedFolderIds(prev =>
                        prev.filter(id => id !== f.entity_id)
                      )
                    } else {
                      setSelectedFolderIds(prev => [...prev, f.entity_id])
                    }
                  } else if (granularity === DAILY) {
                    if (selectedFolderIds.includes(f.entity_id))
                      setSelectedFolderIds([])
                    else setSelectedFolderIds([f.entity_id])
                  }
                }}
                selected={selectedFolderIds.includes(f.entity_id)}
              >
                <ListItemIcon>
                  <Tooltip title={`ID:${f.entity_id}`}>
                    <Avatar src={f?.image_url} sx={{ mr: 2 }} />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText primary={f.name} />
              </ListItemButton>
            ))}
        </List>
      </Section>

      <Section title={'Formatierung'} open={false} sx={{ display: 'none' }}>
        <Select
          fullWidth
          value={locales}
          onChange={event => setLocales(event.target.value)}
        >
          <MenuItem value={'de_DE'}>
            Deutsch (Deutschland) <Chip label={'de_DE'} />
          </MenuItem>
          <MenuItem value={'de_AT'}>
            Deutsch (Österreich) <Chip label={'de_AT'} />
          </MenuItem>
          <MenuItem value={'de_CH'}>
            Deutsch (Schweiz) <Chip label={'de_CH'} />
          </MenuItem>
        </Select>
      </Section>

      <Section title={'Empfänger'} open={false}>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={toEmails}
          inputValue={toInput}
          onInputChange={(e, v) => setToInput(v)}
          onChange={(e, v) => {
            const arr = splitEmails(v.join(','))
            setToEmails(arr)
            setRecipients(prev => ({ ...prev, to: joinEmails(arr) }))
          }}
          renderValue={(value, getItemProps) =>
            value.map((option, key) => (
              <Chip
                key={option}
                label={option}
                {...getItemProps({ index: key })}
                variant={'outlined'}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              label={'Email'}
              onKeyDown={event => {
                if (['Enter', 'Tab', ',', ';', ' '].includes(event.key)) {
                  const val = toInput.trim()
                  if (!val) return
                  event.preventDefault()
                  const arr = addEmailsFromInput(toEmails, val)
                  setToEmails(arr)
                  setRecipients(prev => ({ ...prev, to: joinEmails(arr) }))
                  setToInput('')
                }
              }}
              onBlur={() => {
                const val = toInput.trim()
                if (!val) return
                const arr = addEmailsFromInput(toEmails, val)
                setToEmails(arr)
                setRecipients(prev => ({ ...prev, to: joinEmails(arr) }))
                setToInput('')
              }}
            />
          )}
        />

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={ccEmails}
          inputValue={ccInput}
          onInputChange={(e, v) => setCcInput(v)}
          onChange={(e, v) => {
            const arr = splitEmails(v.join(','))
            setCcEmails(arr)
            setRecipients(prev => ({ ...prev, cc: joinEmails(arr) }))
          }}
          renderValue={(value, getItemProps) =>
            value.map((option, key) => (
              <Chip
                key={option}
                label={option}
                {...getItemProps({ index: key })}
                variant={'outlined'}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              label={'CC'}
              onKeyDown={event => {
                if (['Enter', 'Tab', ',', ';', ' '].includes(event.key)) {
                  const val = ccInput.trim()
                  if (!val) return
                  event.preventDefault()
                  const arr = addEmailsFromInput(ccEmails, val)
                  setCcEmails(arr)
                  setRecipients(prev => ({ ...prev, cc: joinEmails(arr) }))
                  setCcInput('')
                }
              }}
              onBlur={() => {
                const val = ccInput.trim()
                if (!val) return
                const arr = addEmailsFromInput(ccEmails, val)
                setCcEmails(arr)
                setRecipients(prev => ({ ...prev, cc: joinEmails(arr) }))
                setCcInput('')
              }}
            />
          )}
        />

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={bccEmails}
          inputValue={bccInput}
          onInputChange={(e, v) => setBccInput(v)}
          onChange={(e, v) => {
            const arr = splitEmails(v.join(','))
            setBccEmails(arr)
            setRecipients(prev => ({ ...prev, bcc: joinEmails(arr) }))
          }}
          renderValue={(value, getItemProps) =>
            value.map((option, key) => (
              <Chip
                key={option}
                label={option}
                {...getItemProps({ index: key })}
                variant={'outlined'}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              label={'BCC'}
              onKeyDown={event => {
                if (['Enter', 'Tab', ',', ';', ' '].includes(event.key)) {
                  const val = bccInput.trim()
                  if (!val) return
                  event.preventDefault()
                  const arr = addEmailsFromInput(bccEmails, val)
                  setBccEmails(arr)
                  setRecipients(prev => ({ ...prev, bcc: joinEmails(arr) }))
                  setBccInput('')
                }
              }}
              onBlur={() => {
                const val = bccInput.trim()
                if (!val) return
                const arr = addEmailsFromInput(bccEmails, val)
                setBccEmails(arr)
                setRecipients(prev => ({ ...prev, bcc: joinEmails(arr) }))
                setBccInput('')
              }}
            />
          )}
        />
      </Section>

      <Stack direction={'row'} spacing={2} alignItems={'center'}>
        <Button
          onClick={onClick}
          variant={'contained'}
          disabled={
            running ||
            foo?.folders.length === 0 ||
            selectedFolderIds.length === 0
          }
        >
          {running ? 'Läuft ...' : 'Export starten'}
        </Button>
      </Stack>

      {running && (
        <Alert severity={'info'} sx={{ mt: 2 }}>
          <AlertTitle>Zähler Informationen werden exportiert ...</AlertTitle>
          <Typography>{fmtLiveDur(startedAt, finishedAt)}</Typography>
        </Alert>
      )}

      {jobId && (
        <Alert severity={'info'} sx={{ mt: 2 }}>
          <Typography>
            Hinweis: Die Erstellung des Exports dauert in Einzelfällen bis zu 10
            Minuten. Ein Schließen der Seite ist jederzeit möglich. Der Bericht
            wird nach Abschluss per E-Mail übermittelt.
          </Typography>
        </Alert>
      )}
    </KPage>
  )
}
